import { fromEvent } from 'rxjs';
import { takeUntil, switchMap, map } from 'rxjs/operators';
import { isString } from 'lodash-es';
import { hasKeysOfType } from '@bayer-int/portletce-shared';

import type { ThemeService } from 'portlet/services';

import { STYLE_TAG_PREFIX } from '../components';

export type Dimensions = {
    height: number;
    width: number;
};

type DimensionChangeHandler = (dim: Dimensions) => void;
type SvelteUseDirective<T = undefined> = (
    element: HTMLElement,
    params: T,
) => Partial<{ update: (params: T) => void; destroy: () => void }>;
type SvelteUseDirectiveNoParams = (
    element: HTMLElement,
) => Partial<{ update: () => void; destroy: () => void }>;

export const watchResize: SvelteUseDirective<DimensionChangeHandler> = (element, callback) => {
    let previousDimensions: Dimensions | null = null;

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const { width, height } = entry.contentRect;
        const dimensions: Dimensions = { width, height };

        if (
            previousDimensions &&
            previousDimensions.height === dimensions.height &&
            previousDimensions.width === dimensions.width
        )
            return;

        previousDimensions = dimensions;
        callback(dimensions);
    });

    observer.observe(element);

    return {
        destroy: () => observer.disconnect(),
    };
};

export const externalCss: SvelteUseDirective<
    | {
          styleDef: string | { css: string } | unknown;
          onStyleSheetChange?: (element: HTMLStyleElement | null) => void;
          service: ThemeService;
          isMounted: boolean;
      }
    | undefined
> = (element, options) => {
    let currentStylesheet = isString(options?.styleDef)
        ? options?.styleDef
        : hasKeysOfType(options?.styleDef, isString, 'css')
          ? (options?.styleDef as { css: string })?.css
          : '';
    const insertStyleTag = (element: HTMLElement, css: string | undefined) => {
        if (!css) return;
        const style = document.createElement('style');
        style.setAttribute('id', STYLE_TAG_PREFIX);
        style.appendChild(document.createTextNode(css));
        if (options?.onStyleSheetChange) options?.onStyleSheetChange(style);
        // Since we are using svelte 5, we have to add <style> tag as last sibling of <article> for correctly override styles, not as child
        element.appendChild(style);
        const parent = element.parentNode; // shadowRoot.
        if (parent) parent.appendChild(style);
    };

    const attachStyle = async (
        element: HTMLElement,
        service: ThemeService,
        currentStylesheet: string,
        isMounted: boolean,
    ) => {
        if (!isMounted) return;
        service
            .getThemeCss(currentStylesheet)
            .then(res => res.data && insertStyleTag(element, res.data))
            .catch(() => console.warn(`Unable to load stylesheet: ${currentStylesheet}`));
    };

    if (isString(options?.styleDef)) {
        attachStyle(element, options.service, currentStylesheet, options.isMounted);
    } else {
        insertStyleTag(element, currentStylesheet);
    }

    return {
        update: newOptions => {
            const newStylesheet = isString(newOptions?.styleDef)
                ? newOptions?.styleDef
                : hasKeysOfType(newOptions?.styleDef, isString, 'css')
                  ? (newOptions?.styleDef as { css: string })?.css
                  : undefined;

            element.querySelectorAll(`#${STYLE_TAG_PREFIX}`).forEach(roitheme => {
                roitheme.remove();
            });
            if (!newStylesheet) {
                if (options?.onStyleSheetChange) options?.onStyleSheetChange(null);
                return;
            }
            currentStylesheet = newStylesheet;

            if (isString(newOptions?.styleDef)) {
                attachStyle(element, newOptions.service, currentStylesheet, newOptions.isMounted);
            } else {
                insertStyleTag(element, currentStylesheet);
            }
        },
    };
};

export const embedInParent: SvelteUseDirectiveNoParams = element => {
    const { top: currentTop, left: currentLeft } = parseInitialPos(getComputedStyle(element));
    const { top, left } = element.parentElement?.getBoundingClientRect() || { top: 0, left: 0 };

    element.style.top = `${top + currentTop}px`;
    element.style.left = `${left + currentLeft}px`;

    return {};
};

export const draggable: SvelteUseDirective<string | undefined> = (element, selector) => {
    const draggableElement: HTMLElement = (selector && element.querySelector(selector)) || element;

    const mouseDown$ = fromEvent<MouseEvent>(draggableElement, 'mousedown');
    const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

    const position$ = mouseDown$.pipe(
        switchMap(event => {
            const { top, left } = parseInitialPos(getComputedStyle(element));

            return mouseMove$.pipe(
                map(({ clientX, clientY }) => ({
                    x: clientX - event.clientX + left,
                    y: clientY - event.clientY + top,
                })),
                takeUntil(mouseUp$),
            );
        }),
    );

    const subscription = position$.subscribe(({ x, y }) => {
        element.style.top = `${y}px`;
        element.style.left = `${x}px`;
    });

    return {
        destroy: () => subscription.unsubscribe(),
    };
};

function parseInitialPos({ left: leftRaw = '', top: topRaw = '' }: CSSStyleDeclaration) {
    const top = topRaw.replace('px', '');
    const left = leftRaw.replace('px', '');

    return {
        top: Number.parseInt(top, 10) || 0,
        left: Number.parseInt(left, 10) || 0,
    };
}
